import { useMemo, useState } from "react";
import { usePageState } from "@/hooks/usePageState";
import { addDays, addMonths, addWeeks, endOfMonth, endOfWeek, startOfMonth, startOfWeek, parseISO, format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { MonthGrid } from "@/components/calendar/MonthGrid";
import { WeekView } from "@/components/calendar/WeekView";
import { TruckGrid } from "@/components/calendar/TruckGrid";
import { CalendarFilters, Legend } from "@/components/calendar/CalendarFilters";
import { DayPanel } from "@/components/DayPanel";
import { useFetch } from "@/hooks/useFetch";
import { toISO, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";

const VIEWS = ["month", "week", "day"];
const EMPTY = { job_type: "", state: "", truck_assigned: "", assigned_team: "" };

export default function CalendarPage() {
  const [view, setView] = usePageState("view", "month");
  const [selected, setSelected] = usePageState("selected", todayISO());
  const [filters, setFilters] = usePageState("filters", EMPTY);
  const anchor = parseISO(selected);

  const range = useMemo(() => {
    if (view === "month") return { start: toISO(startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 })), end: toISO(endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 })) };
    if (view === "week") return { start: toISO(startOfWeek(anchor, { weekStartsOn: 1 })), end: toISO(endOfWeek(anchor, { weekStartsOn: 1 })) };
    return { start: selected, end: selected };
  }, [view, selected, anchor]);

  const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data: cal } = useFetch("/calendar", { ...range, ...active });
  const { data: jobs } = useFetch("/bookings", { date: selected, ...active });
  const capacity = cal?.capacity || 40;
  const days = cal?.days || {};

  const step = (dir) => {
    const fn = view === "month" ? addMonths : view === "week" ? addWeeks : addDays;
    setSelected(toISO(fn(anchor, dir)));
  };
  const title = view === "month" ? format(anchor, "MMMM yyyy") : view === "week" ? `Week of ${format(parseISO(range.start), "d MMM")} – ${format(parseISO(range.end), "d MMM yyyy")}` : format(anchor, "EEEE d MMMM yyyy");
  const openDay = (iso) => { setSelected(iso); setView("day"); };

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="Colours show which routes are running each day. Click a day to see every job.">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="calendar-view-switch">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`} className={cn("rounded-md px-3.5 py-1.5 text-sm font-medium capitalize transition-colors", view === v ? "bg-[#0E1E38] text-white" : "text-slate-600 hover:text-slate-900")}>{v}</button>
          ))}
        </div>
        <input type="date" value={selected} onChange={(e) => e.target.value && setSelected(e.target.value)} data-testid="calendar-date-picker" className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" />
      </PageHeader>
      <CalendarFilters filters={filters} setFilters={setFilters} />
      <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5">
        <h2 className="font-display text-lg font-semibold text-[#0E1E38]" data-testid="calendar-title">{title}</h2>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setSelected(todayISO())} data-testid="calendar-today">Today</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {view === "month" && <MonthGrid anchor={anchor} selected={selected} onSelect={openDay} days={days} capacity={capacity} />}
      {view === "week" && <><WeekView start={range.start} days={days} onSelectDay={openDay} /><TruckGrid start={range.start} days={days} /></>}
      {view === "day" && <DayPanel date={selected} jobs={jobs || []} capacity={capacity} />}
      <Legend />
    </div>
  );
}
